import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaBriefcase, FaGraduationCap, FaEnvelope } from "react-icons/fa";
import { Helmet } from "react-helmet";
import "./Careers.css";

// Lazy load GSAP (optional, kept for potential future animations)
const loadGSAP = () => import("gsap").then((module) => module.gsap);
const loadScrollTrigger = () =>
  import("gsap/ScrollTrigger").then((module) => module.ScrollTrigger);

const Careers = () => {
  const [gsapLoaded, setGsapLoaded] = useState(false);
  // const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("India");

  /* -------- COUNTRY LOCATIONS -------- */
  const countryLocations = {
    India: "Chennai & Hyderabad",
    USA: "Wyoming, USA",
    UK: "London, UK",
  };

  /* -------- JOB LISTINGS -------- */
  const jobListings = useMemo(
    () => [
      {
        title: "Full Stack Developer",
        description:
          "Looking for an all-rounder to work on both frontend and backend of web applications.",
        icon: <FaBriefcase className="careers-job-icon" />,
      },
      {
        title: "AI / ML Engineer",
        description:
          "Design, develop, and deploy machine learning models and AI-driven solutions.",
        experience: "1–5 Years",
        skills: [
          "Python, Machine Learning, Deep Learning",
          "TensorFlow / PyTorch / scikit-learn",
          "SQL & Data Processing",
          "Cloud basics (AWS/Azure/GCP)",
          "API & Deployment knowledge",
        ],
        responsibilities: [
          "Build and optimize ML models",
          "Develop data pipelines",
          "Deploy AI solutions in cloud environments",
        ],
        icon: <FaBriefcase className="careers-job-icon" />,
      },
      {
        title: "Technologies Internship Programs",
        description:
          "Gain hands-on experience in Software Development, DevOps, AWS, and Azure. Industry-recognized certifications included.",
        icon: <FaGraduationCap className="careers-job-icon" />,
      },
    ],
    [],
  );

  /* -------- ANIMATION VARIANTS -------- */
  const animationVariants = useMemo(
    () => ({
      cardInitial: { opacity: 0, y: 40 },
      cardAnimate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
      },
      hoverEffect: { scale: 1.02, transition: { duration: 0.2 } },
      tapEffect: { scale: 0.98, transition: { duration: 0.1 } },
    }),
    [],
  );

  /* -------- GSAP (optional) -------- */
  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const [gsap, ScrollTrigger] = await Promise.all([
          loadGSAP(),
          loadScrollTrigger(),
        ]);
        gsap.registerPlugin(ScrollTrigger);
        gsap.fromTo(
          ".careers-main-heading",
          { opacity: 0, y: -20 },
          { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" },
        );
        setGsapLoaded(true);
      } catch {}
    };
    setTimeout(loadAnimations, 100);
  }, []);

  /* -------- STRUCTURED DATA -------- */
  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "DB4Cloud",
      url: "https://db4cloud.in",
    }),
    [],
  );

  return (
    <div className="careers-container">
      <Helmet>
        <title>Careers at DB4Cloud - Join Our Team</title>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="careers-main-container">
        <h1 className="careers-main-heading">Join Our Team</h1>

        {/* -------- COUNTRY SELECTOR (pills) -------- */}
        <div className="careers-country-selector">
          {Object.keys(countryLocations).map((country) => (
            <button
              key={country}
              className={`careers-country-pill ${
                selectedCountry === country ? "active" : ""
              }`}
              onClick={() => setSelectedCountry(country)}
            >
              {country}
            </button>
          ))}
        </div>

        {/* -------- JOB LISTINGS (appear after country selection) -------- */}
        <AnimatePresence>
          {selectedCountry && (
            <motion.section
              key={selectedCountry}
              className="careers-jobs-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {jobListings.map((job, index) => (
                <motion.article
                  key={index}
                  className="careers-job-card"
                  variants={animationVariants}
                  initial="cardInitial"
                  animate="cardAnimate"
                  whileHover="hoverEffect"
                  whileTap="tapEffect"
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="careers-job-icon-wrapper">{job.icon}</div>
                  <h3 className="careers-job-title">{job.title}</h3>
                  <p className="careers-job-description">{job.description}</p>
                  <p className="careers-job-location">
                    <strong>Location:</strong>{" "}
                    {countryLocations[selectedCountry]}
                  </p>

                  {job.experience && (
                    <div className="careers-job-extra">
                      <p>
                        <strong>Experience:</strong> {job.experience}
                      </p>
                      <div>
                        <strong>Key Skills:</strong>
                        <ul className="careers-job-list">
                          {job.skills.map((skill, i) => (
                            <li key={i}>{skill}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong>Responsibilities:</strong>
                        <ul className="careers-job-list">
                          {job.responsibilities.map((res, i) => (
                            <li key={i}>{res}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </motion.article>
              ))}
            </motion.section>
          )}
        </AnimatePresence>

        {/* -------- APPLY SECTION (appears after country selection) -------- */}
        {selectedCountry && (
          <motion.section
            className="careers-apply-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="careers-apply-title">How to Apply</h2>
            <p className="careers-apply-text">
              Interested candidates can submit their resumes to:
            </p>
            <div className="careers-apply-contact">
              <FaEnvelope className="careers-apply-icon" />
              <a
                href="mailto:careers@db4cloud.in"
                className="careers-apply-email"
              >
                careers@db4cloud.in
              </a>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default Careers;
